import React, { useEffect, useRef, useState } from "react";
import Button from "../components/base/Button";
import FormField from "../components/FormField";
import { Logo } from "../components/App";
import { errorMessage } from "../shared/utils/validators";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthService from "../services/AuthService";
import Icons from "../components/Icons";
import { Alert, Snackbar } from "@mui/material";
import { OAuthProviderButtons } from "./OAuthProviderButtons";
import { persistAuthTokenFromResponse } from "../shared/authToken";
import { passwordSchema } from "../shared/utils/formikValidators";
import "./authPages.css";

// ORK-165: Email is in the shared signup spec, but WappGrow web POST /signup does not
// forward `email` to createAccount (only /m1/signup does). Do not collect email here
// until the web signup route accepts it — documented as a backend blocker.

function SignUpPage() {
  const $name = useRef();
  const [visible, setVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isOtpView, setOtpView] = useState(false);
  const [mobileVerificationId, setMobileVerificationId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    document.title = "Create account · WappGrow";
  }, []);

  const schema = Yup.object().shape({
    firstName: Yup.string()
      .trim()
      .required("Required name")
      .max(50, "Name should not exceed 50 characters"),
    mobile: Yup.string()
      .required("Required mobile number")
      .matches(/^[0-9]{10,15}$/, "Enter a valid mobile number (10–15 digits, with country code if needed)"),
    password: passwordSchema,
  });

  const otpSchema = Yup.object().shape({
    otp: Yup.string()
      .required("OTP is required")
      .matches(/^[0-9]{4,6}$/, "Enter a valid OTP"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      mobile: "",
      password: "",
    },
    validationSchema: schema,
    onSubmit: handleGetStarted,
  });

  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpSchema,
    onSubmit: handleOtpVerify,
  });

  useEffect(() => {
    $name.current && $name.current.focus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get("oauth_error");
    if (oauthErr) {
      setSnackbar({
        open: true,
        message:
          "Google or Facebook sign-up did not complete. Configure OAuth on the server, or sign up with your number and password.",
        severity: "error",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  async function handleGetStarted(values) {
    setFormSubmitted(true);
    try {
      const res = await AuthService.signupVerificationCode(
        values.mobile.trim(),
        values.firstName.trim()
      );
      if (res.id?.mobileVerificationId) {
        setMobileVerificationId(res.id.mobileVerificationId);
      }
      setOtpView(true);
    } catch (error) {
      const message = errorMessage(error);
      setSnackbar({
        open: true,
        message: message || error.response?.data?.error || "Something went wrong",
        severity: "error",
      });
    }
  }

  async function handleOtpVerify({ otp }) {
    setFormSubmitted(true);
    try {
      const payload = {
        firstName: formik.values.firstName.trim(),
        lastName: formik.values.lastName.trim(),
        mobile: formik.values.mobile.trim(),
        password: formik.values.password.trim(),
        mobileVerificationCode: mobileVerificationId ? otp : null,
        mobileVerificationId,
      };
      await AuthService.createAccount(payload);
      window.location.replace("/");
    } catch (error) {
      setSnackbar({
        open: true,
        message: errorMessage(error) || "Something went wrong",
        severity: "error",
      });
    }
  }

  function snackBar() {
    return (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-card">
          <Logo />
          <h1 className="auth-title">{isOtpView ? "Verify OTP" : "Create account"}</h1>
          <p className="auth-subtitle">
            {isOtpView
              ? `Enter the OTP sent to ${formik.values.mobile}`
              : "Create your WappGrow account to start managing WhatsApp."}
          </p>

          {!isOtpView ? (
            <form onSubmit={formik.handleSubmit} className="auth-form">
              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  ref={$name}
                  type="auth-text"
                  name="firstName"
                  placeholder="Name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.firstName
                    && formik.errors.firstName
                  }
                />
              </div>

              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  type="auth-text"
                  name="mobile"
                  placeholder="Mobile"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.mobile
                    && formik.errors.mobile
                  }
                />
              </div>
              <p className="auth-field-hint">Digits only, with country code if needed</p>

              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  autoComplete="off"
                  name="password"
                  placeholder="Password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    (formSubmitted || formik.submitCount > 0)
                    && formik.touched.password
                    && formik.errors.password
                  }
                  type={visible ? "text" : "password"}
                  iconSuffix={
                    formik.values.password && (
                      <div
                        style={{ cursor: "pointer", display: "flex" }}
                        onClick={() => setVisible(!visible)}
                        aria-label={visible ? "Hide password" : "Show password"}
                      >
                        {visible ? Icons.Visible : Icons.VisibleOff}
                      </div>
                    )
                  }
                  maxLength={25}
                />
              </div>
              <p className="auth-field-hint">
                8+ characters with upper and lower case letters and a number
              </p>

              <Button type="submit" appearance="auth-btn" className="auth-primary-btn">
                Create account
              </Button>

              <OAuthProviderButtons
                onError={(message) => {
                  setSnackbar({
                    open: true,
                    message: message || "Social sign-in failed",
                    severity: "error",
                  });
                }}
                onAuthenticated={async (body) => {
                  persistAuthTokenFromResponse(body);
                  window.location.replace("/");
                }}
              />

              <p className="auth-terms">
                By continuing, you agree to our{" "}
                <a
                  className="ui-link auth-terms-link"
                  href="https://orkaapps.com/links/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  className="ui-link auth-terms-link"
                  href="https://orkaapps.com/links/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          ) : (
            <form onSubmit={otpFormik.handleSubmit} className="auth-form">
              <div className="auth-field-wrap auth-focus-input">
                <FormField
                  type="auth-text"
                  name="otp"
                  placeholder="Enter OTP"
                  value={otpFormik.values.otp}
                  onChange={otpFormik.handleChange}
                  onBlur={otpFormik.handleBlur}
                  error={
                    (formSubmitted || otpFormik.submitCount > 0)
                    && otpFormik.touched.otp
                    && otpFormik.errors.otp
                  }
                  style={{
                    textAlign: "center",
                    letterSpacing: "0.22em",
                  }}
                />
              </div>
              <Button type="submit" appearance="auth-btn" className="auth-primary-btn">
                Verify and create account
              </Button>
            </form>
          )}

          <div className="auth-footer">
            Already have an account?
            <a href="/signin" className="ui-link auth-footer-link">
              Sign in
            </a>
          </div>
          {snackBar()}
        </div>
      </div>
    </div>
  );
}

export { SignUpPage };
